/**
 * Command Parser - Uses OpenAI to extract intent and entities from natural language
 */

import OpenAI from 'openai';
import { ParsedCommand, CommandIntent } from './types';
import { OPENAI_API_KEY } from '../constants';

const openai = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true, // Note: In production, use a backend proxy
});

const SYSTEM_PROMPT = `You are an AI assistant that helps admin users manage a home maintenance application database. Your job is to parse natural language commands into structured operations.

Available Operations:
1. Service Requests: Query, create, or update service requests (repair/maintenance requests from homeowners)
2. Admin Tasks: Query, create, or update admin tasks (internal tasks for the admin team)
3. Users: Look up user information, query users, or update user details (lifecycle status, home manager, etc.)
4. Inventory: Query, create, or update home inventory items

Key Entities:
- Users can be identified by: full name, email, user ID, or phone number
- Service requests/tasks can be identified by: ID, title keywords, or associated user
- Status values: pending, in_progress, completed, cancelled
- Priority values: low, normal, high, urgent
- Lifecycle status: lead, onboarding, active, churned
- Home managers: Bradley Curtis, Brandon Takens, Bryce Kaiser, Unassigned

Extract the intent and all relevant entities from the user's command.`;

interface OpenAIFunctionArgs {
  intent: CommandIntent;
  confidence: number;
  target_id?: string;
  target_user?: string;
  target_email?: string;
  target_home_id?: string;
  filters?: Record<string, any>;
  updates?: Record<string, any>;
  new_record?: Record<string, any>;
  clarification_needed?: string;
  suggested_questions?: string[];
}

const functions: OpenAI.Chat.Completions.ChatCompletionCreateParams.Function[] = [
  {
    name: 'parse_admin_command',
    description: 'Parse an admin command into structured intent and entities',
    parameters: {
      type: 'object',
      properties: {
        intent: {
          type: 'string',
          enum: [
            'query_service_requests',
            'update_service_request',
            'create_service_request',
            'query_admin_tasks',
            'create_admin_task',
            'update_admin_task',
            'lookup_user',
            'query_users',
            'update_user',
            'query_inventory',
            'create_inventory_item',
            'update_inventory_item',
            'help',
            'clarification_needed'
          ],
          description: 'The primary intent of the command'
        },
        confidence: {
          type: 'number',
          description: 'Confidence level from 0-1 in the parsing accuracy',
          minimum: 0,
          maximum: 1
        },
        target_id: {
          type: 'string',
          description: 'ID of the specific record to target (service request, task, user, or inventory item)'
        },
        target_user: {
          type: 'string',
          description: 'Name of the user to target'
        },
        target_email: {
          type: 'string',
          description: 'Email of the user to target'
        },
        target_home_id: {
          type: 'string',
          description: 'ID of the home to target'
        },
        filters: {
          type: 'object',
          description: 'Filter criteria for queries',
          properties: {
            status: {
              oneOf: [
                { type: 'string' },
                { 
                  type: 'array',
                  items: { type: 'string' }
                }
              ],
              description: 'Filter by status (can be single or multiple)'
            },
            priority: {
              oneOf: [
                { type: 'string' },
                { 
                  type: 'array',
                  items: { type: 'string' }
                }
              ],
              description: 'Filter by priority (can be single or multiple)'
            },
            home_manager: {
              type: 'string',
              description: 'Filter by assigned home manager'
            },
            lifecycle_status: {
              type: 'string',
              description: 'Filter by user lifecycle status'
            }
          }
        },
        updates: {
          type: 'object',
          description: 'Fields to update in the target record'
        },
        new_record: {
          type: 'object',
          description: 'Data for creating a new record'
        },
        clarification_needed: {
          type: 'string',
          description: 'Explanation of what needs clarification if the command is ambiguous'
        },
        suggested_questions: {
          type: 'array',
          items: { type: 'string' },
          description: 'Suggested clarifying questions to ask the user'
        }
      },
      required: ['intent', 'confidence']
    }
  }
];

export async function parseCommand(input: string): Promise<ParsedCommand> {
  try {
    const completion = await openai.chat.completions.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: input }
      ],
      functions,
      function_call: { name: 'parse_admin_command' },
      temperature: 0.3,
    });

    const functionCall = completion.choices[0]?.message?.function_call;
    
    if (!functionCall || !functionCall.arguments) {
      throw new Error('No function call returned from OpenAI');
    }

    const args: OpenAIFunctionArgs = JSON.parse(functionCall.arguments);

    const parsedCommand: ParsedCommand = {
      intent: args.intent,
      confidence: args.confidence,
      entities: {
        target_id: args.target_id,
        target_user: args.target_user,
        target_email: args.target_email,
        target_home_id: args.target_home_id,
        filters: args.filters,
        updates: args.updates,
        new_record: args.new_record,
      },
      originalInput: input,
      clarificationNeeded: args.clarification_needed,
      suggestedQuestions: args.suggested_questions,
    };

    return parsedCommand;
  } catch (error) {
    console.error('Error parsing command:', error);
    
    // Return a clarification-needed result on error
    return {
      intent: 'clarification_needed',
      confidence: 0,
      entities: {},
      originalInput: input,
      clarificationNeeded: 'I had trouble understanding that command. Could you rephrase it?',
      suggestedQuestions: [
        'Try being more specific about what you want to do',
        'Include relevant IDs or names if you have them',
      ],
    };
  }
}

export function isHighConfidence(command: ParsedCommand): boolean {
  return command.confidence >= 0.7;
}

export function needsClarification(command: ParsedCommand): boolean {
  return command.intent === 'clarification_needed' || command.confidence < 0.5;
}
